import {
    BellOutlined, CalendarOutlined, CheckOutlined, LockOutlined, LogoutOutlined,
    GlobalOutlined, NotificationOutlined, ReloadOutlined, SettingOutlined, UserOutlined,
} from '@ant-design/icons';
import { Avatar, Badge, Button, Divider, Empty, Image, List, Popover, Segmented, Space, Tag, Typography, notification as toast } from 'antd';
import { Header } from 'antd/es/layout/layout';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { UPDATE_TOKEN, UPDATE_USER } from '../../../../Redux/actions/userActions';
import { apiCall } from '../../../../API/RestApi';
import { useI18n } from '../../../../Utils/i18n';

const { Text } = Typography;

const cleanNotificationText = (value) => {
    let text = String(value || '').trim();
    text = text
        .replace(/\s*\(\s*ID\s*:\s*\d+\s*\)/gi, '')
        .replace(/\bID\s*:\s*\d+\b/gi, '')
        .replace(/\bID\s+\d+\b/gi, '')
        .replace(/\b(record|data)\s+id\s*[:#]?\s*\d+\b/gi, '')
        .replace(/\bbackend\b/gi, 'sistem')
        .replace(/\bback-end\b/gi, 'sistem')
        .replace(/\bapi\b/gi, 'sistem')
        .replace(/\bendpoint\b/gi, 'fitur')
        .replace(/\bdatabase\b/gi, 'sistem')
        .replace(/\bserver\b/gi, 'sistem')
        .replace(/\bcontroller\b/gi, 'fitur')
        .replace(/\s{2,}/g, ' ')
        .trim();
    return text;
};

const normalizeNotificationLink = (url, type, title, message) => {
    const raw = String(url || '').trim();
    if (raw === '/salary' || raw === '/payroll/employee') return '/payslip';
    if (raw) return raw;
    const key = `${type || ''} ${title || ''} ${message || ''}`.toLowerCase();
    if (key.includes('payroll') || key.includes('gaji') || key.includes('payslip')) return '/payslip';
    if (key.includes('warning') || key.includes('teguran')) return '/warningletters/me';
    if (key.includes('overtime') || key.includes('lembur')) return '/overtime';
    if (key.includes('leave') || key.includes('cuti')) return '/leavepaid';
    if (key.includes('attendance') || key.includes('absen')) return '/attendance';
    if (key.includes('reimbursement')) return '/approvalreimbursement';
    return '/notification';
};

function Headers() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((state) => state.user.token);
    const s = useSelector((state) => state.user.user);
    let user = {};
    try { user = JSON.parse(s) || {}; } catch (_) { user = {}; }

    const { t, language, setLanguage } = useI18n();
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread] = useState(0);
    const [notifLoading, setNotifLoading] = useState(false);
    const firstLoad = useRef(true);
    const knownIds = useRef(new Set());
    const [toastApi, contextHolder] = toast.useNotification();

    const menu = [
        { name: t('profile'), link: '/profile', icon: <UserOutlined /> },
        { name: t('settings'), link: '/settings', icon: <SettingOutlined /> },
        { name: t('changePassword'), link: '/changepassword', icon: <LockOutlined /> },
        { name: t('logout'), link: '/logout', icon: <LogoutOutlined />, danger: true },
    ];

    const today = useMemo(() => new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'id-ID', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
    }).format(new Date()), [language]);

    const request = useCallback(async (method, url, payload) => apiCall({
        method, url,
        data: { data: payload, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
    }), [token]);

    const loadNotifications = useCallback(async (showLoader = false) => {
        if (!token || !user?.users_id) return;
        if (showLoader) setNotifLoading(true);
        const res = await request('GET', '/app-notifications?limit=100');
        if (showLoader) setNotifLoading(false);
        if (res?.status !== 'success') return;

        const fallbackLink = (n) => {
            const type = String(n.event_type || n.source_type || '').toLowerCase();
            const text = `${n.title || ''} ${n.message || ''}`.toLowerCase();
            if (type.includes('warning') || text.includes('teguran')) return '/warningletters/me';
            if (type.includes('payroll') || text.includes('gaji') || text.includes('payslip')) return '/payslip';
            if (type.includes('overtime') || text.includes('lembur')) return '/overtime';
            if (type.includes('leave') || text.includes('cuti')) return '/leavepaid';
            if (type.includes('attendance') || text.includes('absen')) return '/attendance';
            if (type.includes('reimbursement')) return '/approvalreimbursement';
                        return '/notification';
        };
        const items = (res.data || []).map(n => ({
            id: n.app_notification_id,
            type: n.event_type || 'info',
            title: cleanNotificationText(n.title || 'Notifikasi'),
            message: cleanNotificationText(n.message || '-'),
            createdAt: n.created_at,
            link: normalizeNotificationLink(n.action_url, n.event_type || n.source_type, n.title, n.message) || fallbackLink(n),
            readAt: n.read_at,
            sourceType: n.source_type,
        }));

        if (!firstLoad.current) {
            const newItems = items.filter(item => !knownIds.current.has(item.id));
            newItems.slice(0, 5).reverse().forEach(item => {
                toastApi.open({
                    key: `app-notification-${item.id}`,
                    message: item.title,
                    description: item.message,
                    placement: 'bottomRight',
                    duration: 7,
                    icon: <NotificationOutlined />,
                    onClick: () => navigate(`/notification/detail/${item.id}`),
                });
            });
        }
        items.forEach(item => knownIds.current.add(item.id));
        firstLoad.current = false;
        setNotifications(items);
        setUnread(Number(res.unread || items.filter(x => !x.readAt).length));
    }, [navigate, request, toastApi, token, user?.users_id]);

    useEffect(() => {
        loadNotifications();
        const interval = setInterval(() => loadNotifications(), 30000);
        const onFocus = () => loadNotifications();
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
    }, [loadNotifications]);

    const markRead = async (id) => {
        const row = notifications.find(n => n.id === id);
        if (!row || row.readAt) return;
        const res = await request('PUT', `/app-notifications/${id}/read`, {});
        if (res?.status === 'success') {
            const now = new Date().toISOString();
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, readAt: now } : n));
            setUnread(v => Math.max(0, v - 1));
        }
    };

    const markAllRead = async () => {
        const res = await request('PUT', '/app-notifications/read-all', {});
        if (res?.status === 'success') {
            const now = new Date().toISOString();
            setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || now })));
            setUnread(0);
        }
    };

    const logout = () => {
        dispatch(UPDATE_TOKEN());
        dispatch(UPDATE_USER());
        navigate('/login');
    };

    const profileContent = (
        <div className="header-profile-popover">
            <div className="header-profile-popover__user">
                <Avatar src={user.image} icon={<UserOutlined />} size={44} />
                <div><Text strong>{user.name || 'User'}</Text><div><Text type="secondary">{user.email || user.username || 'Bolde Access'}</Text></div></div>
            </div>
            <Divider />
            <List split={false} dataSource={menu} renderItem={(item) => (
                <List.Item className="header-profile-popover__item">
                    <Button type="text" danger={item.danger} icon={item.icon} onClick={() => item.link === '/logout' ? logout() : navigate(item.link)}>{item.name}</Button>
                </List.Item>
            )} />
        </div>
    );

    const typeTag = (type) => {
        if (type === 'approved') return <Tag color="green">APPROVED</Tag>;
        if (type === 'rejected') return <Tag color="red">REJECTED</Tag>;
        if (type === 'request') return <Tag color="blue">REQUEST</Tag>;
        if (type === 'warning') return <Tag color="orange">WARNING</Tag>;
        if (type === 'payroll') return <Tag color="purple">PAYROLL</Tag>;
        if (type === 'overtime') return <Tag color="cyan">OVERTIME</Tag>;
        if (type === 'leave') return <Tag color="geekblue">LEAVE</Tag>;
        if (type === 'reimbursement') return <Tag color="gold">REIMBURSE</Tag>;
        if (type === 'attendance') return <Tag color="lime">ATTENDANCE</Tag>;
        return <Tag>INFO</Tag>;
    };

    const notificationContent = (
        <div className="header-notification-popover" style={{ width: 'min(460px, calc(100vw - 32px))' }}>
            <div className="header-notification-popover__title" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <Space><Text strong>{t('notificationsActivity')}</Text><Badge count={unread} /></Space>
                <Space size={2}>
                    <Button type="text" size="small" icon={<ReloadOutlined spin={notifLoading} />} onClick={() => loadNotifications(true)} />
                    <Button type="link" size="small" icon={<CheckOutlined />} disabled={!unread} onClick={markAllRead}>{t('markAllRead')}</Button>
                </Space>
            </div>
            <Divider style={{ margin: '10px 0' }} />
            {notifications.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('noNotifications')} /> : (
                <List style={{ maxHeight: 480, overflowY: 'auto' }} dataSource={notifications} renderItem={(notif) => {
                    const isUnread = !notif.readAt;
                    return <List.Item style={{ padding: '10px 6px', background: isUnread ? 'rgba(24,144,255,.06)' : 'transparent', cursor: 'pointer', borderRadius: 6 }} onClick={async () => { await markRead(notif.id); navigate(`/notification/detail/${notif.id}`); }}>
                        <div style={{ width: '100%' }}>
                            <Space size={6} wrap>{typeTag(notif.type)}<Text strong={isUnread}>{notif.title}</Text>{isUnread && <Badge status="processing" />}</Space>
                            <div style={{ marginTop: 5, whiteSpace: 'normal', wordBreak: 'break-word' }}><Text type="secondary">{notif.message}</Text></div>
                            <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 11 }}>{notif.createdAt ? moment(notif.createdAt).format('DD MMM YYYY HH:mm') : '-'}</Text></div>
                        </div>
                    </List.Item>;
                }} />
            )}
        </div>
    );

    return <>
        {contextHolder}
        <Header className="app-header">
            <div className="app-header__inner">
                <div className="app-header__brand"><Image className="app-header__logo" preview={false} src="/assets/logo.png" /></div>
                <div className="app-header__workspace"><div className="app-header__workspace-badge">BA</div><div><Text className="app-header__workspace-title">{t('employeeWorkspace')}</Text><div className="app-header__workspace-subtitle">Bolde Access • HR & Operations</div></div></div>
                <div className="app-header__right">
                    <div className="app-header__date"><CalendarOutlined /><span>{today}</span></div>
                    <div className="app-header__language" title={t('language')}>
                        <GlobalOutlined className="app-header__language-icon" />
                        <Segmented
                            size="small"
                            value={language}
                            onChange={setLanguage}
                            options={[{ value: 'id', label: 'ID' }, { value: 'en', label: 'EN' }]}
                            aria-label={t('language')}
                        />
                    </div>
                    <Popover placement="bottomRight" content={notificationContent} trigger="click" onOpenChange={(open) => open && loadNotifications()}>
                        <Button className="app-header__icon-button" type="text" aria-label="Notifications"><Badge count={unread} size="small" offset={[2, -2]}><BellOutlined /></Badge></Button>
                    </Popover>
                    <Popover placement="bottomRight" content={profileContent} trigger="click"><button type="button" className="app-header__user-button"><Avatar src={user.image} icon={<UserOutlined />} size={38} /><div className="app-header__user-copy"><span className="app-header__user-name">{user.name || 'User'}</span><span className="app-header__user-role">{t('myAccount')}</span></div></button></Popover>
                </div>
            </div>
        </Header>
    </>;
}

export default Headers;
